/**
 * RailTooltip — a visible label for an icon-only control (the collapsed nav
 * rail), shown on mouse hover and on keyboard focus.
 *
 * Austin, 2026-10-21 (#dev-requests): hovering a rail icon should pop up its
 * title. The label is rendered into document.body at a fixed position beside the
 * trigger, because the rail's <nav> scrolls (overflow-y: auto, which clips its
 * children's absolutely positioned boxes) and the tooltip has to sit outside the
 * 56px rail anyway.
 *
 * Accessibility:
 *   - The trigger keeps its own aria-label: that is its accessible name. The
 *     tooltip is `role="tooltip"` but is deliberately NOT wired up with
 *     aria-describedby, which would make a screen reader say the name twice.
 *   - It never takes focus and never intercepts the pointer (pointer-events:
 *     none), so it cannot trap focus or sit between the mouse and a neighbour.
 *   - Keyboard focus shows it only for :focus-visible, so clicking an icon with
 *     the mouse does not leave a tooltip pinned open.
 *   - Escape dismisses it (WCAG 1.4.13); it comes back on the next hover/focus.
 *   - Touch pointers do not open it: a tap navigates, and there is no hover.
 */

import {
  cloneElement, useCallback, useEffect, useState,
  type FocusEvent, type PointerEvent, type ReactElement,
} from 'react'
import { createPortal } from 'react-dom'

/** Gap between the rail's outer edge and the tooltip, px. */
export const RAIL_TOOLTIP_GAP = 8

interface Anchor { right: number; centerY: number }

interface TriggerProps {
  onPointerEnter?: (e: PointerEvent<HTMLElement>) => void
  onPointerLeave?: (e: PointerEvent<HTMLElement>) => void
  onFocus?: (e: FocusEvent<HTMLElement>) => void
  onBlur?: (e: FocusEvent<HTMLElement>) => void
}

// Horizontally the tooltip hangs off the rail's outer edge (the trigger sits
// inset from it); vertically it centres on the trigger.
function anchorOf(el: HTMLElement): Anchor {
  const r = el.getBoundingClientRect()
  const edge = (el.closest('aside') ?? el).getBoundingClientRect().right
  return { right: edge, centerY: r.top + r.height / 2 }
}

function isFocusVisible(el: HTMLElement): boolean {
  // jsdom and old engines do not know :focus-visible (matches() would throw); a
  // focus event with no way to tell is treated as keyboard so the tooltip errs
  // toward showing. Asked per call, not cached, so a test can flip it.
  const known = typeof CSS !== 'undefined' && typeof CSS.supports === 'function' && CSS.supports('selector(:focus-visible)')
  return known ? el.matches(':focus-visible') : true
}

export default function RailTooltip({ label, enabled = true, children }: {
  label: string
  /** False never shows the tooltip (an expanded row already shows its label); the child keeps its handlers. */
  enabled?: boolean
  /** One element that can take handlers (a <button>). Its own handlers still run. */
  children: ReactElement<TriggerProps>
}) {
  const [hover, setHover] = useState<Anchor | null>(null)
  const [focus, setFocus] = useState<Anchor | null>(null)
  const [dismissed, setDismissed] = useState(false)

  const anchor = hover ?? focus
  const visible = enabled && anchor !== null && !dismissed

  useEffect(() => {
    if (!visible) return
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setDismissed(true) }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [visible])

  const { onPointerEnter, onPointerLeave, onFocus, onBlur } = children.props
  const enter = useCallback((e: PointerEvent<HTMLElement>) => {
    onPointerEnter?.(e)
    if (e.pointerType === 'touch') return
    setDismissed(false)
    setHover(anchorOf(e.currentTarget))
  }, [onPointerEnter])
  const leave = useCallback((e: PointerEvent<HTMLElement>) => {
    onPointerLeave?.(e)
    setHover(null)
  }, [onPointerLeave])
  const focused = useCallback((e: FocusEvent<HTMLElement>) => {
    onFocus?.(e)
    if (!isFocusVisible(e.currentTarget)) return
    setDismissed(false)
    setFocus(anchorOf(e.currentTarget))
  }, [onFocus])
  const blurred = useCallback((e: FocusEvent<HTMLElement>) => {
    onBlur?.(e)
    setFocus(null)
  }, [onBlur])

  // Always the same tree, enabled or not: the handlers stay on the child so a
  // leave/blur that happens while the rail is expanded still clears the state
  // (an early `return children` dropped them, and the next collapse showed a
  // tooltip for a pointer that was long gone). `visible` carries the gate.
  return (
    <>
      {cloneElement(children, {
        onPointerEnter: enter, onPointerLeave: leave, onFocus: focused, onBlur: blurred,
      })}
      {visible && createPortal(
        <div
          role="tooltip"
          data-rail-tooltip
          style={{
            position: 'fixed',
            left: anchor.right + RAIL_TOOLTIP_GAP,
            top: anchor.centerY,
            transform: 'translateY(-50%)',
            zIndex: 1100,
            pointerEvents: 'none',
            padding: '6px 10px',
            borderRadius: 6,
            background: 'var(--color-black)',
            color: 'var(--color-white)',
            fontSize: 12,
            fontWeight: 600,
            lineHeight: 1.2,
            whiteSpace: 'nowrap',
            boxShadow: '0 4px 12px rgba(35,31,32,0.18)',
          }}
        >
          {label}
        </div>,
        document.body,
      )}
    </>
  )
}
